import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Button from '../../common/Button';
import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import { validateRequired, validateEmail, validatePhone } from '../../../utils/validators';
import siteService from '../../../services/siteService';
import managerService from '../../../services/managerService';

const EditTeamLeadModal = ({ isOpen, onClose, onSubmit, teamLead }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    designation: '',
    site_ids: [],
    is_active: true,
    password: '',
    is_locked_out: false,
    supervisor_id: ''
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // State for dynamic dropdowns
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAllSites();
      loadManagers();
    }
  }, [isOpen]);

  const loadManagers = async () => {
    setLoadingManagers(true);
    const result = await managerService.getAllManagers();
    if (result.success) setManagers(result.data);
    setLoadingManagers(false);
  };

  useEffect(() => {
    if (teamLead && isOpen) {
      let initialSiteIds = [];
      // Prefer the 'sites' array which contains the full site objects including IDs
      if (Array.isArray(teamLead.sites)) {
        initialSiteIds = teamLead.sites.map((site) => (site.id || site.site_id)?.toString()).filter(Boolean);
      } else if (Array.isArray(teamLead.site_ids)) {
        initialSiteIds = teamLead.site_ids.map((id) => id.toString());
      } else if (teamLead.site_id) {
        initialSiteIds = [teamLead.site_id.toString()];
      }

      setFormData({
        name: teamLead.name || '',
        email: teamLead.email || '',
        contact: teamLead.contact || '',
        designation: teamLead.designation || '',
        site_ids: initialSiteIds,
        is_active: teamLead.is_active !== undefined ? teamLead.is_active : true,
        password: '',
        is_locked_out: teamLead.is_locked_out !== undefined ? teamLead.is_locked_out : false,
        supervisor_id: (teamLead.supervisor?.id || '').toString()
      });
    }
  }, [teamLead, isOpen]);

  const loadAllSites = async () => {
    setLoadingSites(true);
    const result = await siteService.getAllSites();
    if (result.success) setSites(result.data);
    setLoadingSites(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSitesChange = (newSiteIds) => {
    setFormData(prev => ({ ...prev, site_ids: newSiteIds }));
    if (errors.site_ids) {
      setErrors(prev => ({ ...prev, site_ids: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Name is required';
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }
    
    if (formData.site_ids.length === 0) newErrors.site_ids = 'At least one site must be selected';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      // Convert IDs to correct type for submission
      updateData.site_ids = updateData.site_ids.map((id) => parseInt(id, 10));
      updateData.supervisor_id = updateData.supervisor_id ? parseInt(updateData.supervisor_id, 10) : null;

      await onSubmit(teamLead.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating team lead:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      onClose();
    }
  };

  const siteOptions = sites.map(s => ({ 
    value: s.id.toString(), 
    label: s.name || s.site_name,
    subtitle: s.company?.company_name || s.company_name || 'Individual Site'
  }));

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update Team Lead'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit Team Lead - ${teamLead?.name}`} size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        <Input label="Full Name" name="name" placeholder="e.g., Sarah Jenkins" value={formData.name} onChange={handleChange} error={errors.name} required />
        <Input label="Designation" name="designation" placeholder="Site Team Lead" value={formData.designation} onChange={handleChange} error={errors.designation} />
        <div style={gridStyle}>
          <Input label="Email" name="email" type="email" placeholder="sarah@amc.com" value={formData.email} onChange={handleChange} error={errors.email} required />
          <Input label="Phone Number" name="contact" placeholder="9876543210" value={formData.contact} onChange={handleChange} error={errors.contact} required />
        </div>

        <SearchableMultiSelect
          label="Assigned Sites"
          options={siteOptions}
          selectedValues={formData.site_ids}
          onChange={handleSitesChange}
          error={errors.site_ids}
          loading={loadingSites}
          placeholder="Search sites by name or company..."
          emptyMessage="No sites found."
          required
        />

        <div style={gridStyle}>
          <div className="form-group">
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
              Assign Manager (Optional)
            </label>
            <select
              name="supervisor_id"
              style={{
                width: '100%', padding: '12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)', color: 'var(--text-primary)', outline: 'none'
              }}
              value={formData.supervisor_id}
              onChange={handleChange}
            >
              <option value="">No Manager Assigned</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <Input label="New Password" name="password" type="password" canPeekPassword placeholder="Leave blank to keep unchanged" value={formData.password} onChange={handleChange} error={errors.password} />
        </div>


        <label style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: formData.is_locked_out ? '#fee2e2' : 'var(--surface-light)', border: formData.is_locked_out ? '1px solid #ef4444' : 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '8px'
        }}>
          <input type="checkbox" name="is_locked_out" checked={formData.is_locked_out} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
          <span style={{ fontSize: '14px', color: formData.is_locked_out ? '#ef4444' : 'var(--text-primary)', fontWeight: '500' }}>Account Locked (Uncheck to reinstate access)</span>
        </label>

        {(teamLead?.engineers_count > 0 || teamLead?.active_tickets > 0) && (
          <div style={{
            padding: '12px 16px', background: '#f59e0b20', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '13px', color: '#f59e0b', marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '4px'
          }}>
             <strong>Warning:</strong> This Team Lead currently manages <strong>{teamLead.engineers_count || 0}</strong> engineers and has <strong>{teamLead.active_tickets || 0}</strong> active team tickets.
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditTeamLeadModal;